// ---------------------------------------------------------------------------
// Primitive Definition
// ---------------------------------------------------------------------------

export type PrimitiveType =
  | "string"
  | "number"
  | "date"
  | "relation"
  | "formula"

export interface LevelFilterOption {
  level: number
  label: string
  count: number
}

export interface PrimitiveConfig {
  mapped_to?: string // e.g. "component.internal_id"
  mapping_title?: string // column header for mapped relation IDs, e.g. "Lieferanten-Artikelnr"
  mapping_source_primitive_id?: string // existing column assigned as mapping source
  mapping_for?: string // on source columns: points to relation prim_id this column maps for
  is_group?: boolean // enables grouping on this primitive
  is_hierarchical?: boolean // hierarchical group (tree structure, e.g. component levels)
  group_structure?: "parent_ref" | "horizontal" | "flat" | "pk_prefix" // defines which grouping mode to use
  level_labels?: Record<string, string> // persisted level labels: {"0": "Endprodukt", ...}
  level_filter_options?: LevelFilterOption[] // persisted: expanded filter options for dropdown
  formula?: string // arithmetic expression, e.g. "quantity * unit_price"
  rollup_from?: string // future: rollup target
  hidden?: boolean // hide from PivotTable columns (structural flags)
  _rollupSourceScope?: string // virtual: scope ID the rollup column originates from
}

export interface PrimitiveDef {
  id: string
  scope_id: string
  name: string
  title: string
  description: string | null
  type: PrimitiveType
  is_pk: boolean
  is_visible: boolean
  display_order: number
  config: PrimitiveConfig
  is_custom: boolean
  related_scope_id: string | null
  relation_cardinality: "1:n" | "n:n" | null
}

// ---------------------------------------------------------------------------
// Scope
// ---------------------------------------------------------------------------

export interface ScopeListItem {
  id: string
  name: string
  description: string | null
  is_singleton: boolean
  is_hidden: boolean
  display_order: number
}

export interface AnchorableMapping {
  primitive_id: string
  primitive_title: string
  mapping_title: string
  owner_scope_id: string
  related_scope_id: string
}

export interface Scope extends ScopeListItem {
  primitives: PrimitiveDef[]
  anchorable_mappings?: AnchorableMapping[]
}

// ---------------------------------------------------------------------------
// Record & Values
// ---------------------------------------------------------------------------

export interface PrimitiveValue {
  primitive_id: string
  primitive_name: string
  value: string | null
  source: string
  confidence: number | null
  extraction_type: "explicit" | "implicit" | null
  needs_cleaning: boolean
  confirmed_by_user: boolean
  /** @deprecated Legacy — no longer sent by backend. Use Record.updated_at instead. */
  updated_at?: string
}

export interface RelationTag {
  primitive_id: string
  primitive_name: string
  related_scope_id: string
  count: number
  earliest_updated_at: string | null
}

export interface QmsRecord {
  id: string
  scope_id: string
  created_at: string
  updated_at: string | null
  updated_source: string | null
  values: Record<string, PrimitiveValue> // keyed by primitive_name
  relation_tags: RelationTag[]
  mapping_values: Record<string, string> // relation_prim_name → comma-separated mapped_ids
}

export interface RelationTimestamp {
  source: string | null
  created_at: string | null
}

export interface RecordsPage {
  scope: Scope
  records: QmsRecord[]
  relation_timestamps?: Record<string, RelationTimestamp>
}

// ---------------------------------------------------------------------------
// Transitive relation counts (multi-hop)
// ---------------------------------------------------------------------------

export interface TransitiveScopeInfo {
  scope_id: string
  scope_name: string
  display_order: number
  via_scope_names: string[]
}

export interface TransitiveCounts {
  transitive_scopes: TransitiveScopeInfo[]
  counts: Record<string, Record<string, number>> // record_id → {scope_id: count}
  earliest_timestamps: Record<string, Record<string, string | null>> // record_id → {scope_id: ISO timestamp}
}

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

export interface HistoryEntry {
  id: string
  old_value: string | null
  new_value: string | null
  source: string
  timestamp: string
}

export interface PrimitiveHistoryGroup {
  primitive_id: string
  primitive_name: string
  primitive_title: string
  entries: HistoryEntry[]
}

// ---------------------------------------------------------------------------
// Relation preview
// ---------------------------------------------------------------------------

export interface LinkedRecordStub {
  id: string
  scope_id: string
  pk_value: string | null
  name_value: string | null
  values: Record<string, PrimitiveValue>
  mapped_id: string | null
  relation_id: string | null
  relation_source: string | null
  relation_created_at: string | null
}

export interface RelationPreview {
  primitive_id: string
  primitive_name: string
  related_scope_id: string
  records: LinkedRecordStub[]
  scope_primitives: PrimitiveDef[]
}

// ---------------------------------------------------------------------------
// Write payloads
// ---------------------------------------------------------------------------

export interface PrimitiveValueIn {
  value?: string | null
  source?: string
  confidence?: number | null
  extraction_type?: string | null
  needs_cleaning?: boolean
  confirmed_by_user?: boolean
}

export interface RecordCreateIn {
  values: Record<string, PrimitiveValueIn>
  relations?: Record<string, string[]>
}

export interface RecordUpdateIn {
  values?: Record<string, PrimitiveValueIn>
  add_relations?: Record<string, string[]>
  remove_relations?: Record<string, string[]>
}

// ---------------------------------------------------------------------------
// Ingestion pipeline
// ---------------------------------------------------------------------------

export interface ParsedSheet {
  name: string
  headers: string[]
  rows: string[][]
}

export interface ParsedDocument {
  file_type: "excel" | "text"
  file_name: string
  sheets: ParsedSheet[]
  raw_text: string
}

export interface AnchorMatch {
  record_id: string
  pk_value: string
  name_value: string | null
  score: number // 0.0 – 1.0
  row_index: number | null
  match_type: "pk" | "mapped" | "both"
  mapped_relation_primitive_id: string | null
  mapped_to_record_id: string | null
  mapped_id_value: string | null
}

export interface NewRowCandidate {
  row_index: number
  pk_value: string
}

export interface NearMissWarning {
  record_id: string
  db_pk: string
  doc_pk: string
  doc_row_index: number
  score: number // 0.0 – 1.0
  name_value: string | null
}

export interface AvailableMappingRelation {
  primitive_id: string
  primitive_title: string
  mapping_title: string
  related_scope_id: string | null
}

export interface AnchorOut {
  matches: AnchorMatch[]
  new_rows: NewRowCandidate[]
  near_miss_warnings: NearMissWarning[]
  pk_col_idx: number | null
  pk_col_header: string
  pk_col_score: number
  pk_primitive_id: string | null
  pk_primitive_title: string
  pk_primitive_type: string
  mapping_col_idx: number | null
  mapping_col_header: string
  mapping_col_score: number
  mapping_primitive_id: string | null
  mapping_title: string
  available_mapping_relations: AvailableMappingRelation[]
}

export interface ExplicitPrimitiveMatch {
  primitive_id: string
  primitive_scope_id: string
  primitive_title: string
  primitive_type: string
  matched_header: string
  column_index: number
  confidence: "high" | "medium"
  related_scope_id?: string | null // set for type === 'relation'
  is_hierarchical?: boolean
  group_structure?: "parent_ref" | "horizontal" | "flat" | "pk_prefix" | null
  is_mapping_title?: boolean // matched against a relation's mapping_title
}

export interface ImplicitPrimitiveCandidate {
  primitive_id: string
  primitive_scope_id: string
  primitive_title: string
  primitive_type: string
  instruction?: string // user hint passed to the AI
  related_scope_id?: string | null // set for type === 'relation'
  best_match_header?: string | null // best fuzzy match (even below threshold)
  best_match_column_index?: number | null
  best_match_confidence?: "high" | "medium" | "low" | null
  is_hierarchical?: boolean
  group_structure?: "parent_ref" | "horizontal" | "flat" | "pk_prefix" | null
}

export interface HierarchyColumnCandidate {
  column_index: number
  header: string
  confidence: "high" | "medium"
  role: "level" | "parent_ref" | "pk_prefix"
  separator?: string | null // for pk_prefix: detected separator character
}

export interface DetectOut {
  explicit: ExplicitPrimitiveMatch[]
  implicit: ImplicitPrimitiveCandidate[]
  hierarchy_candidates?: HierarchyColumnCandidate[]
}

// ---------------------------------------------------------------------------
// Group hierarchy
// ---------------------------------------------------------------------------

export interface GroupNode {
  id: string
  scope_id: string
  primitive_id: string
  name: string
  parent_id: string | null
  level: number
  display_order: number
  children: GroupNode[]
  record_count: number
}

export interface GroupTree {
  primitive_id: string
  primitive_title: string
  roots: GroupNode[]
  level_labels?: Record<string, string> // level number (as string) → display label
  level_filter_options?: LevelFilterOption[] // expanded: one entry per distinct value at each level
}

export interface UnconnectedRecord {
  pk: string
  name: string
  label: string
  row: string[] // full row data for display
}

export interface UnconnectedGroup {
  label: string
  count: number
  samples: UnconnectedRecord[]
}

export interface UnconnectedSummary {
  total: number
  headers: string[] // column headers for row data
  groups: UnconnectedGroup[]
}

export interface BuildHierarchyOut {
  tree: GroupTree
  warnings: string[]
  unconnected?: UnconnectedSummary | null
}

/** User's hierarchy extraction config, set during the strategy step */
export interface HierarchyConfig {
  primitive_id: string
  mode: "horizontal" | "flat" | "parent_ref" | "pk_prefix"
  // Horizontal: ordered column indices (index 0 = root level)
  level_columns?: number[]
  // Flat: group name column / Parent-ref: display name column for group nodes
  name_column?: number
  parent_column?: number
  // Parent-ref: each row is a node, parent found by reference columns
  pk_column?: number
  ref_columns?: number[] // ordered reference columns, root→leaf
  level_labels?: string[] // one label per level (len = ref_columns.length + 1)
  // Pk-prefix: hierarchy encoded in PK column via separator
  separator?: string // delimiter within the PK (e.g. "-")
  level_label_column?: number // column whose values name each level (e.g. Materialgruppe)
}

export interface ExtractionCell {
  record_id: string
  primitive_id: string
  primitive_scope_id: string
  value: string | null
  needs_cleaning: boolean
  raw_value: string | null
  conflict_values?: string[] // alternative values from duplicate rows
  mapped_id?: string | null // mapped_id to write on the relation
  existing_value?: string | null
  existing_source?: string | null
  existing_updated_at?: string | null
}

export interface ExtractExplicitOut {
  extractions: ExtractionCell[]
}

export interface ImplicitExtractionOut {
  value: string | null
  reasoning: string
  confidence: "high" | "medium" | "low"
}

export interface CommitOut {
  committed: number
  records_affected: number
  created_records: { id: string; pk_value: string }[]
  id_map: Record<string, string> // provisional_id → real DB id
  import_event_id: string | null
}

// ---------------------------------------------------------------------------
// Import Events & Rollback
// ---------------------------------------------------------------------------

export interface ImportEvent {
  id: string
  scope_id: string
  scope_name: string
  document_name: string
  committed_at: string
  status: "committed" | "rolled_back"
  records_created_count: number
  records_updated_count: number
  relations_created_count: number
  cells_committed: number
}

export interface ChangeEvent {
  id: string
  kind: "bom_setup" | "import" | "manual_edit" | "touch"
  scope_id: string | null
  scope_name: string | null
  document_name: string | null
  timestamp: string
  status: "committed" | "rolled_back"
  records_created_count: number
  records_updated_count: number
  relations_created_count: number
  cells_count: number
}

export interface RollbackResult {
  status: "rolled_back" | "blocked"
  cells_reverted: number
  records_soft_deleted: number
  relations_removed: number
  conflicts: string[]
}

/** A relation that could not be resolved at extract time because the target record
 *  didn't exist yet. Tracked across the wizard session; resolved when a later cascade
 *  creates the target scope's records. Discarded on wizard close if still unresolved. */
export interface DeferredRelation {
  source_record_id: string // real record ID (provisional already resolved via id_map)
  source_scope_id: string // scope that owns the relation primitive
  primitive_id: string
  raw_value: string // original CSV value, used to match against new pk_values
  related_scope_id: string // scope whose records we're waiting for
}

// ---------------------------------------------------------------------------
// Diff Mode (Import Changes)
// ---------------------------------------------------------------------------

/** A single cell difference between file value and DB value */
export interface DiffCell {
  recordId: string
  primitiveId: string
  primitiveName: string
  primitiveTitle: string
  primitiveType: PrimitiveType
  /** Current value in the database (null if new record / empty cell) */
  dbValue: string | null
  /** New value from the imported file */
  fileValue: string | null
  /** Raw value before any cleaning */
  rawFileValue: string | null
  /** Type incompatibility: file value cannot be coerced to the primitive type */
  typeIncompatible: boolean
  /** Acceptance state: null = unresolved, true = accepted new value */
  accepted: boolean | null
  /** Source of the most recent history entry for this cell (e.g. file name or "manual") */
  lastHistorySource?: string | null
  /** ISO timestamp of the most recent history entry */
  lastHistoryTimestamp?: string | null
  /** When true, this is a secondary context cell (not from a file).
   *  Does not count toward stats, auto-commit, or "Alle akzeptieren". */
  secondary?: boolean
  /** When true, the user explicitly declined exporting to this cell.
   *  The document's original value is preserved — nothing is written. */
  declined?: boolean
}

/** Per-record diff summary */
export interface DiffRecord {
  recordId: string
  pkValue: string
  nameValue: string | null
  isNew: boolean
  cells: Record<string, DiffCell> // keyed by primitiveId
  /** Row-level timestamp (Record.updated_at) — used for freshness checks */
  rowUpdatedAt?: string | null
  /** Source of the last row update */
  rowUpdatedSource?: string | null
  /** Row-level freshness acceptance: null = unresolved, true = confirmed.
   *  Confirming a row also accepts all its cells. */
  rowFreshnessAccepted?: boolean | null
}

/** Type validation result for a single column mapping */
export interface ColumnTypeCheck {
  primitiveId: string
  primitiveTitle: string
  primitiveType: PrimitiveType
  columnHeader: string
  columnIndex: number
  compatible: boolean
  /** Sample values that fail type validation */
  incompatibleSamples: string[]
  /** Total count of incompatible values */
  incompatibleCount: number
}

/** A single relation add or remove within a diff */
export interface RelationDiffEntry {
  targetId: string
  targetPkValue: string
  targetNameValue: string | null
  mappedId: string | null
  action: "add" | "remove"
  /** null = unresolved, true = accepted */
  accepted: boolean | null
  /** Source of this change (e.g. file name) */
  source?: string | null
  /** ISO timestamp of the change */
  timestamp?: string | null
  /** Groups entries that represent the same underlying relation change across
   *  scopes (e.g. component→substance add and its substance→component inverse).
   *  Accepting one propagates to all entries sharing this key. */
  mirrorKey?: string
}

/** Relation changes for one record + one relation primitive */
export interface RelationDiff {
  recordId: string
  primitiveId: string
  primitiveName: string
  primitiveTitle: string
  relatedScopeId: string
  currentCount: number
  entries: RelationDiffEntry[]
  /** Cached relation preview from extraction — avoids re-fetching on expand/collapse */
  cachedRelationData?: RelationPreview
}

/** Top-level diff state for a scope */
export interface DiffState {
  scopeId: string
  fileName: string
  /** All records with differences, keyed by recordId */
  records: Record<string, DiffRecord>
  /** Raw extraction entries (needed for commit) */
  rawExtractions: ExtractionEntry[]
  /** Anchored records (needed for commit) */
  anchoredRecords: AnchoredRecord[]
  /** Relation diffs: recordId → primitiveId → RelationDiff */
  relationDiffs?: Record<string, Record<string, RelationDiff>>
  /** When true, diff mode is for export (download file) instead of import (commit to DB) */
  exportMode?: boolean
  /** Original parsed document data — needed for upload-based export to reconstruct the file */
  exportParsedHeaders?: string[]
  exportParsedRows?: string[][]
  exportDelimiter?: string
  /** Target output format for format-based export (no uploaded document) */
  targetFormat?: ExportFormat
  /** Columns chosen in ExportConfigModal — needed for format-based export download */
  exportColumns?: DownloadColumn[]
  /** Record IDs selected for format-based export */
  exportRecordIds?: string[]
}

/** All possible export formats.
 *  The *_konformitaet formats are client-rendered PDFs — jsPDF in the
 *  browser, driven by a dedicated data endpoint under /conformity-letter.
 *  The backend's /export/download does not serve these. */
export type ExportFormat =
  | "csv"
  | "excel"
  | "produkt_konformitaet"
  | "kunden_konformitaet"
  | "escha_kunden_konformitaet"

/** Conformity templates route through handleConformityPdfDownload → jsPDF,
 *  and their column mapping step is preset + read-only. */
export function isConformityFormat(
  fmt: string | undefined,
): fmt is
  | "produkt_konformitaet"
  | "kunden_konformitaet"
  | "escha_kunden_konformitaet" {
  return (
    fmt === "produkt_konformitaet" ||
    fmt === "kunden_konformitaet" ||
    fmt === "escha_kunden_konformitaet"
  )
}

// Working state types used by the IngestionModal

export interface AnchoredRecord {
  record_id: string
  pk_value: string
  name_value: string | null
  row_index: number | null
  is_new: boolean
  additional_row_indices?: number[] // extra rows with same PK (duplicates)
  /** Pair anchor: the related-scope record this row matched via mapped_id.
   *  Only populated in mapping-anchor mode. */
  related_record_id?: string | null
}

export interface UserMapping {
  primitive_id: string
  primitive_scope_id: string
  primitive_title: string
  primitive_type: string
  column_index: number
  related_scope_id?: string | null
  is_mapping_title?: boolean
  isRollup?: boolean
  /** Helper flag + formula config (virtual column, user-authored formula,
   *  only materialized in the diff view and exported files).  Not persisted
   *  to the DB; commit ignores rows with isHelper. */
  isHelper?: boolean
  helperConfig?: {
    label: string
    expression: string
    /** When set, the exported file writes the helper's computed value into
     *  THIS existing doc-header column (replacing whatever was there in the
     *  upload) instead of appending a new column with `label` as header. */
    targetDocHeader?: string | null
  }
}

export interface ExtractionEntry {
  record_id: string
  primitive_id: string
  primitive_scope_id: string
  primitive_title: string
  primitive_type: string
  value: string | null
  raw_value: string | null
  needs_cleaning: boolean
  confidence: number // 0.0 – 1.0
  extraction_type: "explicit" | "implicit"
  reasoning?: string
  related_scope_id?: string | null // set for type === 'relation'
  // conflict detection (populated during review)
  existing_value?: string | null
  existing_source?: string
  existing_updated_at?: string
  // user resolution (true = accept new, false = keep existing)
  accepted: boolean
  // duplicate-row conflicts: alternative raw values from other rows with same PK
  conflict_values?: string[]
  // Export mode: column index in the original document (for file reconstruction)
  export_column_index?: number
  // Mapped relation ID: value to write as mapped_id on the relation
  mapped_id?: string | null
  // Relation action: "add" (default) or "remove"
  action?: "add" | "remove"
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export interface ReachablePrimitive {
  primitive_id: string
  scope_id: string
  scope_name: string
  primitive_name: string
  primitive_title: string
  primitive_type: string
  is_pk: boolean
  is_default: boolean
}

export interface ExportDiffCell {
  record_id: string
  primitive_id: string
  primitive_scope_id: string
  column_index: number
  db_value: string | null
  doc_value: string | null
  db_source: string | null
  db_updated_at: string | null
}

export interface ExportExtractOut {
  diffs: ExportDiffCell[]
}

export interface DownloadColumn {
  primitive_id: string
  scope_id: string
  label: string
  rollup?: boolean
  /** Cross-scope rollup: aggregate values from a related scope's primitive */
  rollupConfig?: {
    sourceScopeId: string
    sourcePrimitiveId: string
    sourcePrimitiveName: string
    aggregation: "concat" | "concat_unique" | "sum" | "count"
  }
  /** Virtual formula column — evaluated client-side against each record's
   *  primitive values.  Carried through the export flow so the diff view
   *  can render the computed output alongside the raw primitives the
   *  formula consumes. */
  helperConfig?: {
    label: string
    expression: string
  }
}

export interface PreviewCell {
  record_id: string
  primitive_id: string
  primitive_scope_id: string
  db_value: string | null
  db_source: string | null
  db_updated_at: string | null
}

export interface PreviewOut {
  cells: PreviewCell[]
}

// ---------------------------------------------------------------------------
// Configuration analysis (Configuration AP document-mode result)
// ---------------------------------------------------------------------------

export interface AnalysisLevel {
  level: number
  label: string
  count?: number
}

export interface AnalysisColumn {
  header: string
  primitive_name?: string
  role: string
  scope?: string
  type?: string
  description?: string
}

export interface AnalysisScope {
  id: string
  primitives: number
  relations: number
}

export interface ConfigurationAnalysis {
  file_name: string
  rows: number
  columns: number
  delimiter: string
  encoding: string
  row_scope: string
  root_is_product: boolean
  summary: string
  document_summary?: string
  hierarchy: {
    mode: string
    separator: string
    levels: AnalysisLevel[]
    level_mode: "column" | "invented"
    level_source_column?: string | null
    ungrouped_count?: number
  }
  column_mappings: AnalysisColumn[]
  scopes: AnalysisScope[]
  schema_yaml?: string
}

// ---------------------------------------------------------------------------
// Record Selection (Select Records wizard)
// ---------------------------------------------------------------------------

export interface DetectConnectedMatch {
  column_index: number
  header: string
  primitive_id: string
  scope_id: string
  scope_name: string
  primitive_title: string
  primitive_type: string
  confidence: "high" | "medium"
  score: number
  is_pk: boolean
}

export interface DetectConnectedOut {
  matches: DetectConnectedMatch[]
  unmatched_columns: number[]
}

export interface SelectMapping {
  primitiveId: string
  scopeId: string
  scopeName: string
  primitiveTitle: string
  primitiveType: string
  header: string
  columnIndex: number
  isPk: boolean
  visible: boolean
  /** Which pkSet (by scopeId+fileName) introduced this mapping */
  originPkSetKey: string
  /** When true, aggregate values from related records instead of listing them */
  isRollup?: boolean
}

export interface PkSet {
  scopeId: string
  pkValues: string[]
  fileNames: string[]
}

export interface RecordSelection {
  pkSets: PkSet[]
  crossScopeOperator: "and" | "or"
  nearMisses: NearMissWarning[]
  visiblePrimitiveIds: string[]
  mappings: SelectMapping[]
}

// ---------------------------------------------------------------------------
// Rollup
// ---------------------------------------------------------------------------

export interface RollupColumnDef {
  primitiveId: string
  primitiveScopeId: string
  primitiveTitle: string
  primitiveType: string
  label: string
  aggregation: "sum" | "concat_unique"
}

export interface RollupCellValue {
  record_id: string
  primitive_id: string
  value: string | null
  source_count: number
  updated_at?: string | null
}

export interface RollupValueEntry {
  value: string | null
  updated_at?: string | null
}

export interface RollupState {
  scopeId: string
  columns: RollupColumnDef[]
  /** Computed + possibly user-edited values: record_id → primitive_id → { value, updated_at } */
  values: Record<string, Record<string, RollupValueEntry>>
  /** Per related scope → which related record IDs are included in the rollup.
   *  When a scope key is absent, all linked records are included (default). */
  includedRecordIds: Record<string, string[]>
}

export interface ConnectedPrimitive {
  primitive_id: string
  scope_id: string
  scope_name: string
  primitive_title: string
  primitive_type: string
  is_pk: boolean
}

// ---------------------------------------------------------------------------
// Unified Action Model
// ---------------------------------------------------------------------------

export type ActionPhase = "select" | "mapping" | "diff" | "done"
export type ActionType = "select" | "import" | "export"

/** Relation mapping for a secondary scope within an action */
export interface ActionScopeMapping {
  /** 'default' = auto-populated, 'user-edited' = user mapped a document column, 'removed' = excluded */
  status: "default" | "user-edited" | "removed"
  /** The relation primitive that connects primary scope to this scope */
  relationPrimitiveId: string
  /** Document column index mapped to this relation (only when user-edited) */
  documentColumn?: number
  /** Placeholder for future rollup/rolldown column mappings */
  additionalMappings: ActionRollupMapping[]
}

/** Placeholder for future rollup/rolldown logic */
export interface ActionRollupMapping {
  type: "rollup" | "rolldown"
  primitiveId: string
  documentColumn?: number
}

/** Parsed file context for a single scope within an action */
export interface ActionFileContext {
  file: File
  parsedDocument?: ParsedDocument
  activeSheet?: ParsedSheet
  mappings?: UserMapping[]
}

/** Per-scope diff state within a multi-scope action */
export interface ScopeDiffState {
  scopeId: string
  fileName: string
  records: Record<string, DiffRecord>
  rawExtractions: ExtractionEntry[]
  anchoredRecords: AnchoredRecord[]
  relationDiffs?: Record<string, Record<string, RelationDiff>>
  exportMode?: boolean
  exportParsedHeaders?: string[]
  exportParsedRows?: string[][]
  exportDelimiter?: string
  targetFormat?: ExportFormat
  exportColumns?: DownloadColumn[]
  exportRecordIds?: string[]
  /** When true, this entire scope diff was auto-generated for context display. */
  secondaryDiff?: boolean
}

/**
 * Unified action state — one global action at a time, shared across all scopes.
 *
 * Replaces: recordSelection, diffState, downloadMode, rollupState, cancelDiffDialogOpen.
 *
 * Lifecycle:
 *   initiate → select → mapping → diff → done
 *   (cancel at any phase discards everything — atomic)
 */
export interface ActionState {
  /** Unique ID per action instance */
  id: string
  /** The scope where the action was initiated — only scope with direct PK selection */
  primaryScopeId: string
  /** Selected PKs in the primary scope (empty = all records) */
  primaryPkValues: string[]
  /** Action type: select records, import changes, or export changes */
  type: ActionType
  /** Current phase of the action lifecycle */
  phase: ActionPhase
  /** True after the first "Work with → Scope" choice; prevents changing primary scope */
  anchorLocked: boolean

  /** Per secondary scope: relation mapping configuration */
  scopeMappings: Record<string, ActionScopeMapping>

  /** One file per scope (select: primary only; import/export: one per qualifying scope) */
  files: Record<string, ActionFileContext>

  /** Diff state per qualifying scope (only populated in 'diff' phase) */
  diffs: Record<string, ScopeDiffState>
}

// ---------------------------------------------------------------------------
// Session architecture (backend: backend/turnus_ai/qms_ingestion/sessions)
// ---------------------------------------------------------------------------

export type SessionStatus =
  | "analyzing"
  | "planning"
  | "awaiting_approval"
  | "executing"
  | "committed"
  | "rolled_back"
  | "partial"
  | "failed"
  | "abandoned"

export type IntentName = "bootstrap" | "refresh_data" | (string & {})

export interface SessionFileMeta {
  name: string
  size: number
  blob_path: string
  uploaded_at: string
}

export interface PlanPrimitiveSpec {
  scope_id: string
  name: string
  type: string
  is_pk: boolean
  title: string
  related_scope_id: string | null
}

export interface PlanRelationSpec {
  from_scope_id: string
  to_scope_id: string
  name: string
  cardinality: string
}

export interface PlanRecordOps {
  create: number
  update: number
  skip: number
}

export interface PlanConflict {
  kind: string
  scope_id: string | null
  message_de: string
  details: Record<string, unknown>
}

export interface SimulatedColumnAnalysis {
  index: number
  header: string
  type: "string" | "number"
  distinct_count: number
  fill_rate: number
  sample_values: string[]
}

export interface SimulatedFileAnalysis {
  filename: string
  row_shape: "one_per_entity" | "cross_product" | "assignment"
  file_role: "master_data" | "junction" | "annotation"
  delimiter: string
  encoding: string
  header_row: number
  row_count: number
  columns: SimulatedColumnAnalysis[]
  pk_candidate_index: number | null
  note_de: string
}

export interface SimulatedCrossRef {
  from_file: string
  from_column_index: number
  from_column_header: string
  to_file: string
  to_column_index: number
  to_column_header: string
  overlap_ratio: number
  kind: "fk_candidate" | "shared_dimension" | (string & {})
}

export interface BundleAnalysis {
  per_file: SimulatedFileAnalysis[]
  cross_refs: SimulatedCrossRef[]
  execution_order: string[]
  note_de: string
}

export interface SessionPlan {
  intent: IntentName
  summary_de: string
  scopes_activated: string[]
  primitives_added: PlanPrimitiveSpec[]
  primitives_modified: unknown[]
  relations_added: PlanRelationSpec[]
  templates_enabled: string[]
  record_ops: Record<string, PlanRecordOps>
  relations_to_create: number
  hierarchy_nodes: number
  warnings: string[]
  conflicts: PlanConflict[]
  rollback_description_de: string
  bundle_analysis: BundleAnalysis | Record<string, never>
}

export interface SessionOut {
  id: string
  customer: string
  intent: IntentName | null
  source: "agent" | "manual"
  status: SessionStatus
  created_at: string
  updated_at: string
  files: SessionFileMeta[]
  analyses: Record<string, unknown>
  plan: SessionPlan | null
  feedback_history: string[]
  executed_event_ids: string[]
  error: string | null
}

export interface SessionEventOut {
  id: string
  scope_id: string
  document_name: string
  committed_at: string
  status: string
  records_created_count: number
  records_updated_count: number
  relations_created_count: number
  primitives_created_count: number
  scopes_activated: string[]
  templates_enabled: string[]
  cells_committed: number
  rows_discarded_count: number
  active_config_before: string | null
  active_config_after: string | null
}

export interface SessionRollbackResult {
  session_id: string
  status: "rolled_back" | "partial" | "failed"
  events_reverted: number
  records_removed: number
  cells_reverted: number
  relations_removed: number
  primitives_removed: number
  errors: string[]
}

// ---------------------------------------------------------------------------
// Importer-AP staging — pending documents and AP settings (v4)
// ---------------------------------------------------------------------------

export interface PendingDocumentOut {
  id: string
  ap_id: string
  target_scope_id: string
  target_record_id: string | null
  target_record_pk: string | null
  target_record_name: string | null
  filename: string
  content_type: string
  size_bytes: number
  source: "upload" | "webhook" | "email" | "monitor"
  uploaded_at: string
}

export interface ApSettingsOut {
  ap_id: string
  webhook_url: string
  webhook_token: string
  email_address: string
}
